import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Menu, LayoutDashboard, FileText, BookOpen, GitMerge, ScanSearch, FlaskConical, History, ShoppingBag, Ticket, FileText as FileTextIcon, PenSquare, Inbox, ScrollText, LogOut, Users } from "lucide-react";
import { enableAdminPwa } from "@/admin/pwa";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const LINKS = [
  ["", "Overview", LayoutDashboard],
  ["documents", "Source Documents", FileText],
  ["rules", "Rules & Interpretations", BookOpen],
  ["reconciliation", "Overlaps & Conflicts", GitMerge],
  ["analyzer", "Quality Control", ScanSearch],
  ["reports", "Reports", FileTextIcon],
  ["golden", "Golden Tests", FlaskConical],
  ["versions", "Ruleset Versions", History],
  ["orders", "Orders", ShoppingBag],
  ["promos", "Promo Codes", Ticket],
  ["content", "Website Content", PenSquare],
  ["waitlist", "India Waitlist", Users],
  ["messages", "Messages", Inbox],
  ["audit", "Audit Log", ScrollText],
];

export default function AdminLayout() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { enableAdminPwa(); }, []);
  useEffect(() => {
    let live = true;
    const back = window.location.pathname + window.location.search;
    api.get("/admin/auth/me").then((r) => live && setMe(r.data)).catch(() => live && nav(`/admin/login?next=${encodeURIComponent(back)}`, { replace: true }));
    return () => { live = false; };
  }, [nav]);
  const qc = useQueryClient();
  const logout = async () => { await api.post("/admin/auth/logout"); qc.invalidateQueries({ queryKey: ["paycfg"] }); nav("/admin/login"); };
  if (!me) return <div className="admin-dark min-h-screen bg-background" />;
  return (
    <div className="admin-dark flex min-h-screen bg-background text-foreground">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-border bg-card/60 md:flex">
        <div className="px-6 py-6">
          <p className="font-serif-display text-2xl">Namonics<span className="text-primary">™</span></p>
          <p className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Administration</p>
        </div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3">
          {LINKS.map(([to, label, Icon]) => (
            <NavLink key={to} to={`/admin/${to}`} end={to === ""} data-testid={`admin-nav-${to || "overview"}`}
              className={({ isActive }) => `flex items-center gap-3 rounded px-3 py-2 text-sm transition-colors ${isActive ? "bg-secondary text-primary" : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"}`}>
              <Icon className="h-4 w-4" /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-4 text-xs text-muted-foreground">
          <p className="truncate" data-testid="admin-user-email">{me.email}</p>
          <button onClick={logout} className="mt-2 inline-flex items-center gap-2 hover:text-foreground" data-testid="admin-logout-btn"><LogOut className="h-3.5 w-3.5" /> Sign out</button>
        </div>
      </aside>
      <div className="flex-1 overflow-x-hidden">
        <div className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-background/95 px-4 py-3 backdrop-blur md:hidden" data-testid="admin-mobile-bar">
          <p className="font-serif-display text-xl">Namonics<span className="text-primary">™</span> <span className="font-mono-ui text-[9px] uppercase tracking-[0.2em] text-muted-foreground">Admin</span></p>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild><button className="rounded border border-border p-2" aria-label="Open menu" data-testid="admin-mobile-menu-btn"><Menu className="h-5 w-5" /></button></SheetTrigger>
            <SheetContent side="left" className="admin-dark w-72 overflow-y-auto border-border bg-background p-0 text-foreground" data-testid="admin-mobile-menu">
              <SheetTitle className="px-6 pb-2 pt-6 font-serif-display text-2xl font-normal">Namonics<span className="text-primary">™</span></SheetTitle>
              <nav className="space-y-0.5 px-3 pb-4">
                {LINKS.map(([to, label, Icon]) => (
                  <NavLink key={to} to={`/admin/${to}`} end={to === ""} onClick={() => setOpen(false)} data-testid={`admin-mnav-${to || "overview"}`}
                    className={({ isActive }) => `flex items-center gap-3 rounded px-3 py-3 text-sm ${isActive ? "bg-secondary text-primary" : "text-muted-foreground"}`}>
                    <Icon className="h-4 w-4" /> {label}
                  </NavLink>
                ))}
              </nav>
              <div className="border-t border-border p-4 text-xs text-muted-foreground">
                <p className="truncate">{me.email}</p>
                <button onClick={logout} className="mt-3 inline-flex items-center gap-2 text-sm" data-testid="admin-mobile-logout-btn"><LogOut className="h-4 w-4" /> Sign out</button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
        <main className="mx-auto max-w-7xl px-4 py-6 sm:px-8 sm:py-8"><Outlet /></main>
      </div>
    </div>
  );
}
